import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseFieldComponent } from '../base-field.component';

@Component({
  selector: 'app-file-field',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="field-wrapper" [class]="field.cssClass" [class.has-error]="isInvalid || !!fileSizeError">

      <label [for]="field.id">
        {{ field.label }}<a *ngIf="hasLabelLink" [href]="field.linkUrl" [target]="field.linkTarget ?? '_blank'" [attr.rel]="(field.linkTarget ?? '_blank') === '_blank' ? 'noopener noreferrer' : null">{{ field.linkText }}</a>
        <span *ngIf="hasRequired" class="required-mark">*</span>
      </label>

      <!-- Zona de carga de archivo -->
      <label
        class="file-upload-area"
        [for]="field.id"
        [class.file-upload-area--has-file]="!!selectedFileName">

        <span class="file-upload-area__icon">📎</span>

        <span *ngIf="!selectedFileName" class="file-upload-area__placeholder">
          {{ field.placeholder || 'Seleccionar archivo' }}
          <span *ngIf="acceptLabel" class="file-upload-area__hint">{{ acceptLabel }}</span>
          <span *ngIf="maxSizeMb" class="file-upload-area__hint">Máx. {{ maxSizeMb }} MB</span>
        </span>

        <span *ngIf="selectedFileName" class="file-upload-area__filename">
          {{ selectedFileName }}
        </span>

        <input
          [id]="field.id"
          type="file"
          class="file-upload-area__input"
          [attr.accept]="acceptAttr || null"
          [attr.aria-describedby]="field.hint ? field.id + '-hint' : null"
          (change)="onFileChange($event)"
        />
      </label>

      <!-- Hint -->
      <small *ngIf="field.hint && !isInvalid && !fileSizeError"
             [id]="field.id + '-hint'">
        {{ field.hint }}
      </small>

      <!-- Error de tamaño -->
      <small *ngIf="fileSizeError" class="error">
        {{ fileSizeError }}
      </small>

      <!-- Error de validación -->
      <small *ngIf="isInvalid && !fileSizeError" class="error">
        {{ errorMessage }}
      </small>

    </div>
  `,
  styles: [`
    .file-upload-area {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 20px 16px;
      border: 2px dashed #cbd5e1;
      border-radius: 8px;
      cursor: pointer;
      background: #f8fafc;
      transition: border-color 0.2s, background 0.2s;
      text-align: center;
      min-height: 80px;
    }
    .file-upload-area:hover {
      border-color: #6366f1;
      background: #eef2ff;
    }
    .file-upload-area--has-file {
      border-color: #6366f1;
      background: #eef2ff;
    }
    .file-upload-area__icon {
      font-size: 24px;
      line-height: 1;
    }
    .file-upload-area__placeholder {
      font-size: 14px;
      color: #64748b;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
    }
    .file-upload-area__hint {
      font-size: 11px;
      color: #94a3b8;
    }
    .file-upload-area__filename {
      font-size: 13px;
      color: #334155;
      font-weight: 500;
      word-break: break-all;
    }
    .file-upload-area__input {
      display: none;
    }
    .has-error .file-upload-area {
      border-color: #ef4444;
      background: #fef2f2;
    }
  `]
})
export class FileFieldComponent extends BaseFieldComponent {

  selectedFileName: string | null = null;
  fileSizeError: string | null = null;

  private get props(): Record<string, unknown> {
    return (this.field?.customProps as Record<string, unknown>) ?? {};
  }

  get acceptAttr(): string {
    return (this.props['accept'] as string) ?? '';
  }

  get maxSizeMb(): number | null {
    const v = this.props['maxSizeMb'];
    return v != null && v !== '' ? Number(v) : null;
  }

  get acceptLabel(): string {
    const a = this.acceptAttr.trim();
    if (!a) return '';
    return a;
  }

  get hasRequired(): boolean {
    return !!this.field.validations?.some(v => v.type === 'required');
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    this.fileSizeError = null;
    this.selectedFileName = null;

    if (!file) {
      this.control.setValue(null);
      this.control.markAsTouched();
      return;
    }

    // Validate size locally
    if (this.maxSizeMb !== null) {
      const maxBytes = this.maxSizeMb * 1024 * 1024;
      if (file.size > maxBytes) {
        this.fileSizeError = `El archivo supera el límite de ${this.maxSizeMb} MB (${(file.size / 1024 / 1024).toFixed(1)} MB).`;
        input.value = '';
        this.control.setValue(null);
        this.control.markAsTouched();
        return;
      }
    }

    // Store the File object in the control — the upload happens on submit
    this.selectedFileName = file.name;
    this.control.setValue(file);
    this.control.markAsTouched();
  }
}
